import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Search, Filter, X, CheckCircle } from 'lucide-react';
import Navbar from '../Navbar';
import OpportunityCard from '../OpportunityCard';
import GuidedTour from '../../Shared/GuidedTour/GuidedTour';

const TOUR_STORAGE_KEY = 'student_dashboard_tour_completed';

const StudentDashboard = ({
  currentUser,
  studentProfile,
  opportunities = [],
  messagingWithAdvisorIds = new Set(),
  companyDisplayName = '',
  brandLogoUrl,
  primaryColor,
  secondaryColor,
  accentColor,
  neutralColor,
}) => {
  const [showProfileBanner, setShowProfileBanner] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    positionType: '',
    location: '',
    remote: false,
  });

  const colors = useMemo(() => ({
    primary: primaryColor || '#5489B6',
    secondary: secondaryColor || '#002949',
    accent: accentColor || '#5B5E5A',
    neutral: neutralColor || '#666666',
  }), [primaryColor, secondaryColor, accentColor, neutralColor]);

  useEffect(() => {
    try {
      const tourCompleted = localStorage.getItem(TOUR_STORAGE_KEY);
      if (!tourCompleted) {
        const timer = setTimeout(() => setShowTour(true), 800);
        return () => clearTimeout(timer);
      }
    } catch (_) {}
  }, []);

  const handleTourClose = () => {
    setShowTour(false);
    try { localStorage.setItem(TOUR_STORAGE_KEY, 'true'); } catch (_) {}
  };

  const handleTourComplete = () => {
    try { localStorage.setItem(TOUR_STORAGE_KEY, 'true'); } catch (_) {}
  };

  const platformName = companyDisplayName ? `${companyDisplayName}'s Hiring Platform` : 'the Hiring Platform';

  const tourSteps = [
    {
      id: 'welcome',
      title: 'Welcome to the Hiring Platform!',
      description: `This is your dashboard — your home base for discovering internship and career opportunities with leading financial advisors on ${platformName}. Let's take a quick tour to help you get started.`,
      targetSelector: '[data-tour="welcome-section"]',
      position: 'bottom',
    },
    {
      id: 'profile',
      title: 'Complete Your Profile',
      description: 'Your profile is how advisors learn about you. A complete profile with your education, skills, and career aspirations increases your chances of getting noticed by 3x. Head to My Profile to stand out.',
      targetSelector: '[data-tour="nav-profile"]',
      position: 'bottom',
    },
    {
      id: 'search',
      title: 'Search Opportunities',
      description: 'Use the search bar to find opportunities that match your interests — whether that\'s wealth management, financial planning, or advisory services.',
      targetSelector: '[data-tour="search-bar"]',
      position: 'bottom',
    },
    {
      id: 'filters',
      title: 'Filter & Refine',
      description: 'Narrow your search by position type, location, or remote-friendly roles. The right opportunity is just a few clicks away.',
      targetSelector: '[data-tour="filters-panel"]',
      position: 'bottom',
    },
    {
      id: 'opportunities',
      title: 'Explore Opportunities',
      description: 'Browse curated opportunities posted by financial advisors looking for motivated students to grow their teams. Click "View Details" to learn more about each position and how to apply.',
      targetSelector: '[data-tour="opportunities-grid"]',
      position: 'top',
    },
    {
      id: 'messages',
      title: 'Connect with Advisors',
      description: 'Reach out directly to advisors through messaging. Build real relationships, explore mentorship, and take the first step toward a career in the advisory profession.',
      targetSelector: '[data-tour="nav-messages"]',
      position: 'bottom',
    },
  ];

  const profileCompletion = studentProfile?.profile_completion_percentage || 0;
  const profileCompleted = studentProfile?.profile_completed || false;

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', positionType: '', location: '', remote: false });
  };

  const hasActiveFilters =
    filters.search || filters.positionType || filters.location || filters.remote;

  const filteredOpportunities = opportunities.filter((opp) => {
    if (
      filters.search &&
      !opp.title.toLowerCase().includes(filters.search.toLowerCase()) &&
      (!opp.description || !opp.description.toLowerCase().includes(filters.search.toLowerCase()))
    ) {
      return false;
    }
    if (filters.positionType && opp.position_type !== filters.positionType) {
      return false;
    }
    if (
      filters.location &&
      !opp.location.toLowerCase().includes(filters.location.toLowerCase())
    ) {
      return false;
    }
    if (filters.remote && !opp.remote) {
      return false;
    }
    return true;
  });

  const handleViewDetails = (opportunityId) => {
    window.location.href = `/student/opportunities/${opportunityId}`;
  };

  const primaryBg10 = `${colors.primary}1A`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar
        currentUser={currentUser}
        currentPath="/student/dashboard"
        brandLogoUrl={brandLogoUrl}
        primaryColor={colors.primary}
        secondaryColor={colors.secondary}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Profile Completion Banner */}
        {showProfileBanner && !profileCompleted && (
          <div
            className="mb-6 border rounded-lg p-4 md:p-6"
            style={{
              background: `linear-gradient(135deg, ${primaryBg10}, ${colors.primary}0D)`,
              borderColor: `${colors.primary}33`,
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-lg">Complete Your Profile</h3>
                  <span className="text-sm font-medium" style={{ color: colors.primary }}>
                    {profileCompletion}% complete
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {profileCompletion === 0
                    ? 'Finish your profile to unlock all opportunities!'
                    : 'A complete profile increases your chances of getting noticed by advisors by 3x!'}
                </p>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{ width: `${profileCompletion}%`, backgroundColor: colors.primary }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{profileCompletion}%</p>
                </div>

                {/* Checklist */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Basic information</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Education details</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                    <span className="text-gray-500">Upload resume</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                    <span className="text-gray-500">Add video introduction</span>
                  </div>
                </div>

                <a
                  href="/student/profile"
                  className="inline-block px-4 py-2 text-white rounded-lg transition-colors font-medium text-sm"
                  style={{ backgroundColor: colors.primary }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                >
                  Complete Profile
                </a>
              </div>
              <button
                onClick={() => setShowProfileBanner(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-6" data-tour="welcome-section">
          <h1 className="text-3xl font-bold mb-2">
            Welcome back, {currentUser?.name?.split(' ')[0] || 'Student'}!
          </h1>
          <p className="text-gray-600">
            Explore opportunities that match your interests and qualifications
          </p>
        </div>

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <div className="relative flex-1" data-tour="search-bar">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search opportunities..."
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                className="w-full h-12 pl-10 pr-4 rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent"
                style={{ '--tw-ring-color': colors.primary }}
                onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center justify-center gap-2 h-12 px-4 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter className="h-5 w-5" />
              Filters
              {hasActiveFilters && (
                <span
                  className="ml-2 h-5 w-5 rounded-full text-white text-xs flex items-center justify-center"
                  style={{ backgroundColor: colors.primary }}
                >
                  !
                </span>
              )}
            </button>
          </div>

          {/* Filters Panel */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:block`}>
            <div className="bg-white border border-gray-200 rounded-lg p-4" data-tour="filters-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Filters</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm font-medium hover:opacity-80"
                    style={{ color: colors.primary }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position Type
                  </label>
                  <select
                    value={filters.positionType}
                    onChange={(e) => updateFilter('positionType', e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none"
                    onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <option value="">All Positions</option>
                    <option value="internship">Internships</option>
                    <option value="full-time">Full-Time</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    placeholder="Location..."
                    value={filters.location}
                    onChange={(e) => updateFilter('location', e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none"
                    onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 h-10 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.remote}
                      onChange={(e) => updateFilter('remote', e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                      style={{ accentColor: colors.primary }}
                    />
                    <span className="text-sm text-gray-700">Remote only</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600">
            {filteredOpportunities.length}{' '}
            {filteredOpportunities.length === 1 ? 'opportunity' : 'opportunities'} found
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 text-sm font-medium hover:opacity-80"
              style={{ color: colors.primary }}
            >
              <X className="h-4 w-4" />
              Clear Filters
            </button>
          )}
        </div>

        {/* Opportunities Grid */}
        {filteredOpportunities.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-tour="opportunities-grid">
            {filteredOpportunities.map((opportunity) => (
              <OpportunityCard
                key={opportunity.id}
                opportunity={opportunity}
                onViewDetails={handleViewDetails}
                isMessagingWithAdvisor={messagingWithAdvisorIds.has(opportunity.user?.id?.toString())}
              />
            ))}
          </div>
        ) : opportunities.length === 0 ? (
          <div className="text-center py-12" data-tour="opportunities-grid">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <svg
                className="h-8 w-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Opportunities Yet</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              There are no opportunities posted at the moment. Check back soon for exciting
              internships and full-time positions!
            </p>
          </div>
        ) : (
          <div className="text-center py-12" data-tour="opportunities-grid">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <Search className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Matches Found</h3>
            <p className="text-gray-600 max-w-md mx-auto mb-4">
              We couldn't find any opportunities matching your filters. Try adjusting your search
              criteria.
            </p>
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-white rounded-lg transition-colors font-medium"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Guided Tour */}
      <GuidedTour
        isOpen={showTour}
        steps={tourSteps}
        onClose={handleTourClose}
        onComplete={handleTourComplete}
        onReplayRequest={() => setShowTour(true)}
      />
    </div>
  );
};

StudentDashboard.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    email: PropTypes.string,
  }),
  studentProfile: PropTypes.shape({
    profile_completion_percentage: PropTypes.number,
    profile_completed: PropTypes.bool,
  }),
  opportunities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      position_type: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      remote: PropTypes.bool,
      description: PropTypes.string,
      user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        allowed_name: PropTypes.string,
      }),
    })
  ),
  messagingWithAdvisorIds: PropTypes.instanceOf(Set),
  companyDisplayName: PropTypes.string,
  brandLogoUrl: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  accentColor: PropTypes.string,
  neutralColor: PropTypes.string,
};

export default StudentDashboard;
